<template>
  <div>
    <div class="row team-page" ref="teamPage">
      <div class="members-list col-xl-3 col-lg-4 col-md-4">
        <div
          class="member-item-container"
          v-for="(item, id) in members"
          :key="id"
        >
          <div
            class="member-item"
            :class="{ active: item === activeMember }"
            :id="item.name.toLowerCase().replace(' ', '')"
            @click="setActive(id)"
          >
            <div class="thumbnail-sm">
              <img
                :src="item.photo"
                :alt="item.name + ' | Trilon Consulting'"
              />
            </div>
            <div class="item-details">
              <span
                v-if="item.twitter"
                :href="'https://www.twitter.com/' + item.twitter"
                rel="noopener"
                target="_blank"
                class="trilon-pink twitter-link"
                >@{{ item.twitter }}</span
              >
              <h5>{{ item.name }}</h5>
              <span class="title">{{ item.title }}</span>
            </div>
            <arrow
              class="d-md-none open-trigger"
              :class="{ active: item === activeMember && isOpenedMobile }"
            />
          </div>
          <div
            class="member-wrapper-mobile col-12 d-md-none"
            :class="{ 'd-none': activeMember !== item }"
            v-if="isOpenedMobile"
          >
            <TeamMemberLarge :member="item" />
          </div>
        </div>
      </div>
      <div class="member-details col-xl-9 col-lg-8 col-md-8 d-md-block d-none">
        <TeamMemberLarge :member="activeMember" />
      </div>
    </div>

    <div class="join-trilon">
      <div class="gradient-wrapper text-center">
        <div class="join-wrapper">
          <h2>
            Interested in joining the Team<span class="trilon-pink">?</span>
          </h2>
          <p class="pt-3">
            Our Team spans all across the globe, with a wide range of expertise
            from everything from design to devops.
          </p>
          <br />
          <a href="/careers" class="btn btn-primary">We're Hiring!</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
definePageMeta({
  layout: 'pages',
  pageKey: 'team',
  pageTitle: 'The Trilon Team',
  subTitle: 'See the team!',
});
const { $store: store } = useNuxtApp();
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', (ev) => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        // this.$scrollTo(hash.toLowerCase().replace(' ', ''), 500, '.members-list');
        const teamMember = document.querySelector(
          hash.toLowerCase().replace(' ', '')
        );
        if (teamMember) {
          teamMember.click();
        }
      }, 500);
    }
  });
}

const structuredData = ref({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://trilon.io/#organization',
      name: 'Trilon',
      url: 'https://trilon.io/',
      sameAs: [],
      logo: {
        '@type': 'ImageObject',
        '@id': 'https://trilon.io/#logo',
        url: 'https://trilon.io/trilon-logo-clear.png',
        caption: 'Trilon',
      },
      image: {
        '@id': 'https://trilon.io/#logo',
      },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://trilon.io/#website',
      url: 'https://trilon.io/',
      name: 'Trilon',
      publisher: {
        '@id': 'https://trilon.io/#organization',
      },
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://trilon.io/?search={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'WebPage',
      '@id': 'https://trilon.io/blog#webpage',
      url: 'https://trilon.io/blog',
      inLanguage: 'en-US',
      name: 'Next-level consulting from NestJS and Angular Universal core team members.',
      isPartOf: {
        '@id': 'https://trilon.io/#website',
      },
      about: {
        '@id': 'https://trilon.io/#organization',
      },
      image: {
        '@type': 'ImageObject',
        '@id': 'https://trilon.io/#primaryimage',
        url: 'https://trilon.io/meta/og-image.png',
        caption: 'Trilon',
      },
      primaryImageOfPage: {
        '@id': 'https://trilon.io/#primaryimage',
      },
      datePublished: '2019-02-24T10:30:12+00:00',
      dateModified: '2019-07-17T11:28:59.031Z',
      description:
        'Next-level consulting from NestJS and Angular Universal core team members. We give teams the push they need to truly succeed in today&#x27;s ever-changing world.',
    },
  ],
});
let activeId = ref(0);
let isOpenedMobile = ref(false);
const blogPosts = computed(() => {
  return store.blogPosts
});
let teamPage = ref(null);
const activeMember = computed(() => {
  const active = store.teamMembers[activeId.value || 0]
  return active;
});
const members = computed(() => {
  return store.teamMembers
});

function setActive(id) {
  if (id === activeId.value) {
    isOpenedMobile = !this.isOpenedMobile;
  } else {
    isOpenedMobile = true;
  }
  if (
    window.pageYOffset > teamPage.offsetTop &&
    window.innerWidth > 767
  ) {
    this.$nextTick(() => {
      teamPage.scrollIntoView && teamPage.scrollIntoView();
    });
  }
  activeId.value = id;
}
onMounted(() => {
  teamPage.value.focus();
});

useHead({
  title: 'The Trilon Team',
  meta: [
    // hid is used as unique identifier. Do not use `vmid` for it as it will not work
    {
      vmid: 'description',
      name: 'description',
      content:
        'Meet the Trilon Team, composed of industry experts, Google Developer Experts, and open-source maintainers. All ready to tackle your applications toughest challenges - right by your side.',
    },
  ],
  __dangerouslyDisableSanitizers: ['script'],
  script: [
    {
      innerHTML: JSON.stringify(structuredData),
      type: 'application/ld+json',
    },
  ],
});
</script>

<style lang="scss" scoped>
.team-page {
  margin-top: 3rem;
  margin-bottom: 8rem;
  scroll-margin-top: 50px;

  @media (max-width: 1300px) {
    margin-top: 0;
  }
  @media (max-width: 992px) {
    margin-bottom: 5rem;
  }
}
.join-trilon {
  max-width: 1000px;
  margin: 3rem auto 10rem;

  .gradient-wrapper {
    padding: 5px;
  }

  @media (max-width: 767px) {
    margin: 9rem auto;
  }

  @media (max-width: 600px) {
    margin: 9rem auto 3rem;
  }
}
.join-wrapper {
  padding: 4rem 8rem;

  p {
    line-height: 28px;
  }

  @media (max-width: 992px) {
    padding: 6rem 4rem;
  }

  @media (max-width: 600px) {
    padding: 6rem 2rem;
  }
}
.members-list {
  text-align: left;
  padding: 0 2rem;
  height: 800px;
  overflow-y: scroll;
  scrollbar-color: #e91e63 #0c0f20;
  position: sticky;
  top: 20px;

  &::-webkit-scrollbar {
    width: 4px;
    height: 100%;
  }

  &::-webkit-scrollbar-track-piece {
    background-color: #0c0f20;
  }

  &::-webkit-scrollbar-thumb:vertical {
    height: 10px;
    background-color: #e91e63;
  }

  @media (max-width: 1300px) {
    padding: 0 1rem;
  }
  @media (max-width: 1200px) {
    padding-right: 0;
  }
  @media (max-width: 768px) {
    padding-right: 1rem;
    height: auto;
    overflow-y: auto;
  }
}
.member-item {
  padding: 2rem 0;
  position: relative;
  transition: all 60ms ease-in;
  cursor: pointer;

  &::after,
  &::before {
    content: '';
    height: 2px;
    background: #fff;
    position: absolute;
    bottom: 0;
    left: 0;
  }
  &::after {
    transition: all 200ms linear;
    transform-origin: center left;
    transform: scaleX(1);
    background: #e91e63;
    right: 10%;

    @media (max-width: 768px) {
      right: 50%;
    }
  }
  &::before {
    background: #fff;
    right: 10%;

    @media (max-width: 768px) {
      right: 0;
    }
  }

  &:not(.active):not(:hover) {
    opacity: 0.75;

    .thumbnail-sm {
      border: 2px solid #41365f;
    }

    &::after {
      transform: scaleX(0);
    }
    &::before {
      background: #2c2c2c;
    }

    @media (max-width: 768px) {
      opacity: 1;

      &::after {
        transform: scaleX(1);
      }
    }
  }

  &:hover:not(.active) {
    &::after {
      transform: scaleX(0.5);
    }
  }
}
.thumbnail-sm,
.item-details {
  display: inline-block;
  vertical-align: middle;
}
.thumbnail-sm {
  // workaround
  // ref: https://stackoverflow.com/questions/16687023/bug-with-transform-scale-and-overflow-hidden-in-chrome
  transform: translate3d(0, 0, 0);
  transition: all 100ms ease-in;
  border-radius: 50%;
  width: 65px;
  height: 65px;
  overflow: hidden;
  border: 2px solid #e91e63;
  position: relative;

  img {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    max-width: 60px;
  }
}
.item-details {
  width: calc(100% - 75px);
  box-sizing: border-box;
  padding-left: 1rem;

  h5 {
    font-size: 1.1rem;
    margin: 0.1rem 0 0;
  }
  .title,
  .twitter-link {
    font-weight: 500;
  }
  .twitter-link {
    font-size: 0.85rem;
  }
  .title {
    font-size: 0.9rem;
  }
}
.member-wrapper-mobile {
  padding: 2.5rem 15px;
}
.open-trigger {
  position: absolute;
  right: 20px;
  top: 60%;
  transform: translateY(-50%) rotate(90deg);
  width: 1.4rem;

  &.active {
    transform: translateY(-50%) rotate(-90deg);
  }
}
</style>
